import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './swipeable-card.module.css';
import { cn } from '../../utils/cn';
import type { SwipeableCardProps } from './swipeable-card.types';
import Button from '../../atoms/button';

const SWIPE_START_THRESHOLD = 16;
const VERTICAL_CANCEL_THRESHOLD = 12;
const HORIZONTAL_PRIORITY_OFFSET = 8;

export const SwipeableCard: React.FC<SwipeableCardProps> = ({
    children,
    buttonProps,
    maxSwipeWidth = 80,
    containerClassName,
    isSwipeable = true,
    contentTestId,
}) => {
    const startX = useRef(0);
    const startY = useRef(0);
    const startTranslateX = useRef(0);
    const currentTranslateX = useRef(0);

    const isTouching = useRef(false);
    const isHorizontalSwipe = useRef<boolean | null>(null);

    const frameRef = useRef<number | null>(null);
    const pendingTranslateRef = useRef(0);

    const [translateX, setTranslateX] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    const maxTranslate = -maxSwipeWidth;

    const flushTranslate = useCallback(() => {
        frameRef.current = null;
        setTranslateX(pendingTranslateRef.current);
    }, []);

    const scheduleTranslate = useCallback(
        (nextTranslate: number) => {
            pendingTranslateRef.current = nextTranslate;

            if (frameRef.current === null) {
                frameRef.current = window.requestAnimationFrame(flushTranslate);
            }
        },
        [flushTranslate]
    );

    const cancelScheduledFrame = useCallback(() => {
        if (frameRef.current !== null) {
            window.cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
    }, []);

    const handleTouchStart = useCallback(
        (e: React.TouchEvent) => {
            if (!isSwipeable) return;

            const touch = e.touches[0];
            if (!touch) return;

            startX.current = touch.clientX;
            startY.current = touch.clientY;
            startTranslateX.current = currentTranslateX.current;
            isTouching.current = true;
            isHorizontalSwipe.current = null;
            setIsAnimating(false);
        },
        [isSwipeable]
    );

    const handleTouchMove = useCallback(
        (e: React.TouchEvent) => {
            if (!isSwipeable || !isTouching.current) return;

            const touch = e.touches[0];
            if (!touch) return;

            const deltaX = touch.clientX - startX.current;
            const deltaY = touch.clientY - startY.current;
            const absX = Math.abs(deltaX);
            const absY = Math.abs(deltaY);

            if (isHorizontalSwipe.current === null) {
                if (absY > VERTICAL_CANCEL_THRESHOLD && absY >= absX) {
                    isHorizontalSwipe.current = false;
                    isTouching.current = false;
                    cancelScheduledFrame();
                    return;
                }

                if (absX > SWIPE_START_THRESHOLD && absX > absY + HORIZONTAL_PRIORITY_OFFSET) {
                    isHorizontalSwipe.current = true;
                } else {
                    return;
                }
            }

            if (!isHorizontalSwipe.current) {
                return;
            }

            e.preventDefault();

            let nextTranslate = startTranslateX.current + deltaX;

            if (nextTranslate < maxTranslate) {
                nextTranslate = maxTranslate;
            }

            if (nextTranslate > 0) {
                nextTranslate = 0;
            }

            currentTranslateX.current = nextTranslate;
            scheduleTranslate(nextTranslate);
        },
        [cancelScheduledFrame, isSwipeable, maxTranslate, scheduleTranslate]
    );

    const finishSwipe = useCallback(() => {
        if (!isSwipeable) return;

        cancelScheduledFrame();

        const wasHorizontalSwipe = isHorizontalSwipe.current === true;

        isTouching.current = false;
        isHorizontalSwipe.current = null;

        if (!wasHorizontalSwipe) {
            return;
        }

        const openThreshold = Math.min(maxSwipeWidth * 0.5, 40);
        const shouldOpen = Math.abs(currentTranslateX.current) > openThreshold;
        const finalTranslate = shouldOpen ? maxTranslate : 0;

        currentTranslateX.current = finalTranslate;
        pendingTranslateRef.current = finalTranslate;
        setIsAnimating(true);
        setTranslateX(finalTranslate);
    }, [cancelScheduledFrame, isSwipeable, maxSwipeWidth, maxTranslate]);

    const handleTouchEnd = useCallback(() => {
        finishSwipe();
    }, [finishSwipe]);

    const handleTouchCancel = useCallback(() => {
        finishSwipe();
    }, [finishSwipe]);

    useEffect(() => {
        return () => {
            cancelScheduledFrame();
        };
    }, [cancelScheduledFrame]);

    const visibleActionWidth = Math.min(Math.abs(translateX), maxSwipeWidth);

    return (
        <div className={cn(styles.container, containerClassName)}>
            <Button
                showIf={!!buttonProps && isSwipeable}
                className={cn(styles.delete, buttonProps?.className, isAnimating && styles.animating)}
                style={{
                    opacity: visibleActionWidth > 0 ? 1 : 0,
                    width: `${visibleActionWidth}px`,
                }}
                {...buttonProps}
            />

            <div
                data-testid={contentTestId}
                className={cn(styles.content, isAnimating && styles.animating)}
                style={{ transform: `translate3d(${translateX}px, 0, 0)` }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchCancel}
            >
                {children}
            </div>
        </div>
    );
};
